import { amber, cyan, deepPurple, yellow } from "@mui/material/colors";
import { LQG_THRESHOLD_HAZARDOUS_LB } from "encamp-shared/src/constants/wasteGeneratorStatusThresholds";
import { useCallback, useMemo } from "react";

import { getWasteThresholdsForState, WasteThreshold } from "../../wasteThresholds";

export type ThresholdWithDisplay = WasteThreshold & {
  id: string;
  color: string;
  displayValue: string;
};

export function useWasteThresholds(facilityState?: string | null) {
  // Get thresholds for the facility's state
  const thresholds = useMemo(() => {
    return getWasteThresholdsForState(facilityState || "");
  }, [facilityState]);

  // Create threshold ID for consistent referencing
  const getThresholdId = useCallback((threshold: WasteThreshold) => {
    return `${threshold.type}-${threshold.threshold}-${threshold.acute ? "acute" : "regular"}`;
  }, []);

  // Get color for a threshold based on its type and properties
  const getThresholdColor = useCallback((threshold: WasteThreshold) => {
    if (threshold.acute) {
      // Acute thresholds use deep purple
      return deepPurple[400];
    }

    // Non-acute thresholds
    if (threshold.type === "federal") {
      // Federal thresholds: darker cyan for higher values
      return threshold.threshold >= LQG_THRESHOLD_HAZARDOUS_LB ? cyan[700] : amber[700];
    } else {
      // State thresholds: darker purple for higher values
      return threshold.threshold >= LQG_THRESHOLD_HAZARDOUS_LB ? cyan[500] : yellow[700];
    }
  }, []);

  // Format threshold value for display
  const formatThresholdValue = useCallback((value: number) => {
    if (value === 0) return "0 lbs";
    if (value < 10) return `${value} lbs`;
    return `${value.toLocaleString()} lbs`;
  }, []);

  // Create enhanced threshold objects with display properties (excluding zero thresholds)
  const thresholdsWithDisplay: ThresholdWithDisplay[] = useMemo(() => {
    return thresholds
      .filter((threshold) => threshold.threshold > 0)
      .map((threshold) => ({
        ...threshold,
        id: getThresholdId(threshold),
        color: getThresholdColor(threshold),
        displayValue: formatThresholdValue(threshold.threshold),
      }));
  }, [thresholds, getThresholdId, getThresholdColor, formatThresholdValue]);

  // Initialize default threshold visibility (all visible by default)
  const defaultThresholdVisibility = useMemo(() => {
    const visibility: Record<string, boolean> = {};
    thresholdsWithDisplay.forEach((threshold) => {
      visibility[threshold.id] = true;
    });
    return visibility;
  }, [thresholdsWithDisplay]);

  return {
    thresholds: thresholdsWithDisplay,
    defaultThresholdVisibility,
    getThresholdId,
    getThresholdColor,
    formatThresholdValue,
  };
}
